import { FormatType } from '@revelio/iso-utility';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import { FilterId } from '../../filters/types/filters';
import {
  demandSupplyRatioRankInfoText,
  marketTightnessInfoText,
  timeToFillRankInfoText,
  wagePressureRankInfoText,
} from '../market-conditions-info-text';

const items: PositionedItem[] = [
  {
    id: '3df56492-59c9-4dc1-bacf-df4571ba6c49',
    title: 'Market Tightness',
    isTitleManuallySet: true,
    position: { x: 0, y: 0, width: 30, height: 12 },
    info: { text: marketTightnessInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "market_tightness"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'f9e3890a-1a39-4691-8e30-47bd64ec7d94',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 12, width: 10, height: 11 },
    info: { text: demandSupplyRatioRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "demand_supply_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'f01cc284-ee70-4896-b95f-a1423a0d99bc',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 12, width: 10, height: 11 },
    info: { text: wagePressureRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "wage_pressure_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'dd70db98-b6c8-41c6-9aa2-060f1dfb1868',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 12, width: 10, height: 11 },
    info: { text: timeToFillRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "time_to_fill_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '1541b0c9-d162-4484-980b-aef60091fbb6',
    title: 'Demand/Supply',
    position: { x: 0, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['demand_supply'],
      format: FormatType.RATIO,
    },
  },
  {
    id: 'b9c608c8-d199-40b9-a041-2e90fbf1eeed',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['wage_pressure'],
      format: FormatType.PERCENTAGE,
    },
  },
  {
    id: '380af3af-754f-4eb1-b50d-9bdad631a60a',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['time_to_fill'],
      format: FormatType.INTEGER,
    },
  },
  {
    id: 'faabf497-0b4b-4b27-a14e-f4a7ccbe28a7',
    title: 'Postings by Company',
    isTitleManuallySet: true,
    position: { x: 30, y: 0, width: 18, height: 36 },
    plot: {
      chartType: 'grouped-bar',
      secondaryDimension: 'company',
      defaultMetricOptions: ['postings_active'],
      format: FormatType.DECIMAL,
    },
  },
];

const tabContent: TabContent = {
  id: RevelioPageId.ROLE_MARKET_CONDITIONS,
  primaryFilter: { id: FilterId.ROLE },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  items,
};

export const roleMarketConditions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.ROLE_MARKET_CONDITIONS,
  revelioPageId: RevelioPageId.ROLE_MARKET_CONDITIONS,
  name: 'Market Conditions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: RevelioPageId.ROLE_MARKET_CONDITIONS,
      label: 'Market Conditions',
      isActive: true,
      tabContent,
    },
  ],
};
